import { CommunicationStatus, failure, isFailure, pending, success } from '@art-suite/art-core-ts';
import { ErrorWithStatus } from '@kaiizen/zod-less-api';
import { useCallback, useRef, useState } from 'react';
import { QueryCache, useQueryCache } from './QueryCacheProvider';
import { requestWithRetry, RetryOptions } from './requestWithRetry';

export type MutationFunction<Args extends unknown[], Out> = (...args: Args) => Promise<Out>;

export type UseMutationOptions<R> = RetryOptions & {
  onStart?: () => void;
  onSuccess?: (data: R, queryCache: QueryCache) => void;
  onError?: (error: unknown, queryCache: QueryCache) => void;
};

/**
 * The status of the request.
 * @param status - The status of the request. undefined indicates idle
 * @param updatedAt - The timestamp of the last time the request was updated.
 */
export type UseMutationStatus = {
  status?: CommunicationStatus;
  updatedAt: number;
  error?: unknown;
  attempt?: number;
};

type AnyAsyncFunction = (...args: any[]) => Promise<any>;

/**
 * Use this to create a mutation function that can be used to mutate the query cache.
 *
 * Note about onError: if onError is provided, `.mutate` will not ever return a rejected promise - instead it will return undefined for errors. If onError is not provided, the mutation will throw an error if the mutation fails.
 * @param mutateFunction - The function to mutate the query cache.
 * @param options - The options for the mutation.
 * @returns The mutation function.
 */
export const useQueryCacheMutation = <F extends AnyAsyncFunction>(
  mutateFunction: F,
  options: UseMutationOptions<Awaited<ReturnType<F>>> = {},
) => {
  const { onStart, onSuccess, onError, ...retryOptions } = options;
  const queryCache = useQueryCache();
  const [requestStatus, setRequestStatus] = useState<UseMutationStatus>({ status: undefined, updatedAt: Date.now() });
  const cancelRetriesRef = useRef<undefined | (() => void)>(undefined);

  const mutate = useCallback(async (...args: Parameters<F>): Promise<Awaited<ReturnType<F>>> => {
    const concurrencyManager = queryCache.concurrencyManager;
    setRequestStatus({ status: pending, updatedAt: Date.now() });
    try {
      onStart?.();
      cancelRetriesRef.current?.();
      const result = await requestWithRetry(
        concurrencyManager,
        ({ cancelRetries: _cancelRetries, updatedAt, attempt }) => {
          setRequestStatus({ status: pending, updatedAt, attempt });
          cancelRetriesRef.current = _cancelRetries;
          return mutateFunction(...args);
        },
        retryOptions,
      );
      onSuccess?.(result, queryCache);
      setRequestStatus({ status: success, updatedAt: Date.now() });
      return result;
    } catch (error) {
      onError?.(error, queryCache);
      setRequestStatus({
        status: error instanceof ErrorWithStatus ? error.communicationStatus : failure,
        updatedAt: Date.now(),
        error: error,
      });
      if (!onError) throw error;
      return undefined as any; // SO, NOTE, TYPING THIS IS HARD - THIS IS CORRECT - if onError is provided, we don't want to throw or else users will get unhandled promise errors - The expectation is they'll be using this in a React component, and the on error will be updating some state, and they don't want to have additional error handlers.
    } finally {
      cancelRetriesRef.current = undefined;
    }
  }, [queryCache, mutateFunction, onStart, onSuccess, onError, retryOptions]);

  const cancel = useCallback(() => {
    cancelRetriesRef.current?.();
    setRequestStatus({ status: undefined, updatedAt: Date.now() });
  }, []);

  return {
    ...requestStatus,
    mutate,
    cancel,
    isIdle: requestStatus.status === undefined,
    isPending: requestStatus.status === pending,
    isSuccess: requestStatus.status === success,
    isError: !!requestStatus.status && isFailure(requestStatus.status),
  };
};
